import type {
  Appearance,
  StripeCheckoutLoadActionsSuccess,
  StripePaymentElement,
} from '@stripe/stripe-js';
import { Banner, Button, Modal, Skeleton, TextField } from '@ths/ui';
import { FormEvent, useEffect, useId, useRef, useState } from 'react';

import { loadHotelCardStripe } from './hotelCardStripe';
import { HotelCardSetup } from './useHotelCardSetup';

type CardFormState = {
  clientSecret: string;
  ready: boolean;
  saving: boolean;
};

function CardFormSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading secure card form"
      className="space-y-4"
    >
      <span className="sr-only">Loading secure card form…</span>
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-11 w-full" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-11 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-11 w-full" />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-11 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-11 w-full" />
      </div>
    </div>
  );
}

/** Stripe's frames cannot inherit CSS variables, so resolve the THS tokens. */
function cardAppearance(container: HTMLElement): Appearance {
  const style = getComputedStyle(container);
  const color = (token: string) => {
    const value = style.getPropertyValue(`--color-${token}`).trim();
    return value ? `rgb(${value})` : undefined;
  };
  return {
    theme: 'stripe',
    variables: {
      colorPrimary: color('accent'),
      colorBackground: color('surface'),
      colorText: color('text'),
      colorTextSecondary: color('text-secondary'),
      colorTextPlaceholder: color('placeholder'),
      colorDanger: color('error-text'),
      fontFamily: style.fontFamily,
      fontSizeBase: style.fontSize,
    },
  };
}

function StripeCardForm({
  clientSecret,
  formId,
  onComplete,
  onError,
  onStateChange,
  isCurrent,
}: {
  clientSecret: string;
  formId: string;
  onComplete: () => Promise<void>;
  onError: () => void;
  onStateChange: (state: CardFormState) => void;
  isCurrent: () => boolean;
}) {
  const container = useRef<HTMLDivElement>(null);
  const actions = useRef<StripeCheckoutLoadActionsSuccess>();
  const active = useRef(false);
  const pending = useRef(false);
  const callbacks = useRef({ onComplete, onError, onStateChange, isCurrent });
  callbacks.current = { onComplete, onError, onStateChange, isCurrent };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [emailValue, setEmailValue] = useState('');
  const [collectEmail, setCollectEmail] = useState(false);

  useEffect(() => {
    let current = true;
    const isActive = () => current && callbacks.current.isCurrent();
    let payment: StripePaymentElement | undefined;
    let fieldsReady = false;
    const showForm = () => {
      if (!isActive() || !fieldsReady || !actions.current) return;
      setLoading(false);
      callbacks.current.onStateChange({
        clientSecret,
        ready: true,
        saving: false,
      });
    };
    active.current = true;
    callbacks.current.onStateChange({
      clientSecret,
      ready: false,
      saving: false,
    });
    void (async () => {
      try {
        const stripe = await loadHotelCardStripe();
        if (!isActive()) return;
        if (!stripe || !container.current)
          throw new Error('Stripe unavailable');
        const checkout = stripe.initCheckoutElementsSdk({
          clientSecret,
          elementsOptions: { appearance: cardAppearance(container.current) },
        });
        payment = checkout.createPaymentElement({
          layout: { type: 'accordion', radios: 'never' },
          wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
          fields: {
            billingDetails: {
              name: 'always',
              email: 'never',
              phone: 'auto',
              address: 'auto',
            },
          },
        });
        payment.on('ready', () => {
          fieldsReady = true;
          showForm();
        });
        payment.on('loaderror', () => {
          if (isActive()) callbacks.current.onError();
        });
        payment.mount(container.current);
        const result = await checkout.loadActions();
        if (!isActive()) return;
        if (result.type !== 'success') throw new Error('Stripe unavailable');
        actions.current = result.actions;
        const knownEmail = result.actions.getSession().email;
        setEmailValue(knownEmail ?? '');
        setCollectEmail(!knownEmail);
        showForm();
      } catch {
        if (isActive()) callbacks.current.onError();
      }
    })();
    return () => {
      current = false;
      active.current = false;
      actions.current = undefined;
      payment?.destroy();
    };
  }, [clientSecret]);

  async function save(event: FormEvent) {
    event.preventDefault();
    // Portal events still bubble to the checkout's booking form.
    event.stopPropagation();
    if (
      !callbacks.current.isCurrent() ||
      !actions.current ||
      pending.current ||
      loading
    )
      return;
    pending.current = true;
    setError(undefined);
    callbacks.current.onStateChange({
      clientSecret,
      ready: true,
      saving: true,
    });
    try {
      const result = await actions.current.confirm({
        redirect: 'if_required',
        returnUrl: window.location.href,
        ...(collectEmail ? { email: emailValue.trim() } : {}),
      });
      if (!active.current || !callbacks.current.isCurrent()) return;
      if (
        result.type !== 'success' ||
        result.session.status.type !== 'complete'
      )
        throw new Error('Card not saved');
      await callbacks.current.onComplete();
    } catch {
      if (active.current && callbacks.current.isCurrent())
        setError(
          'Unable to save this card. Check the card details and try again.',
        );
    } finally {
      pending.current = false;
      if (active.current && callbacks.current.isCurrent())
        callbacks.current.onStateChange({
          clientSecret,
          ready: true,
          saving: false,
        });
    }
  }

  return (
    <form
      id={formId}
      onSubmit={(event) => void save(event)}
      className="relative space-y-3"
      aria-busy={loading}
    >
      {loading && <CardFormSkeleton />}
      <div
        className={loading ? 'invisible absolute inset-x-0 top-0' : undefined}
        aria-hidden={loading || undefined}
      >
        {collectEmail && (
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={emailValue}
            onChange={(event) => setEmailValue(event.target.value)}
            disabled={loading || pending.current}
          />
        )}
        <div ref={container} />
      </div>
      {error && <Banner status="error">{error}</Banner>}
    </form>
  );
}

export function HotelCardSetupModal({ setup }: { setup: HotelCardSetup }) {
  const state = setup.state;
  const formId = useId();
  const [formState, setFormState] = useState<CardFormState>();
  const currentForm =
    formState?.clientSecret === state?.clientSecret ? formState : undefined;
  const syncing = state?.phase === 'syncing';
  const saving = state?.phase === 'entry' && currentForm?.saving === true;
  return (
    <Modal
      open={!!state}
      dismissible={!saving && !syncing}
      onOpenChange={(open) => {
        if (!open && !saving && !syncing) setup.close();
      }}
      title="Add a card"
      className="max-w-lg"
      returnFocusRef={setup.returnFocus}
      footer={
        <>
          <Button
            type="button"
            variant="neutral"
            onClick={setup.close}
            disabled={saving || syncing}
          >
            Cancel
          </Button>
          {state?.phase === 'entry' && (
            <Button
              type="submit"
              form={formId}
              disabled={!currentForm?.ready || saving}
            >
              {saving ? 'Saving card…' : 'Save card'}
            </Button>
          )}
          {state?.error && (
            <Button
              type="button"
              onClick={() =>
                void (state.phase === 'saved-error'
                  ? setup.complete()
                  : setup.addCard())
              }
            >
              Try again
            </Button>
          )}
        </>
      }
    >
      {state?.phase === 'starting' && <CardFormSkeleton />}
      {syncing && (
        <p role="status" className="py-4 text-text-secondary">
          Updating saved cards…
        </p>
      )}
      {state?.error && <Banner status="error">{state.error}</Banner>}
      {state?.phase === 'entry' && state.clientSecret && (
        <StripeCardForm
          key={state.clientSecret}
          clientSecret={state.clientSecret}
          formId={formId}
          onComplete={setup.complete}
          onError={setup.formError}
          onStateChange={setFormState}
          isCurrent={setup.isCurrent}
        />
      )}
    </Modal>
  );
}
